import { IssueTransferDialog } from "~/components/issueDetailsPage/IssueTransferDialog"
import { useMutationState } from "@tanstack/react-query"
import { getEditorContentFingerprint } from "~/lib/editor-fingerprint"
import { CustomEmoji } from "~/components/CustomEmoji"
import type { CommentReaction } from "~/lib/emoji"
import { IssueProjectPicker } from "~/components/issueDetailsPage/IssueProjectPicker"
import { IssueHistoryDialog } from "~/components/issueDetailsPage/IssueHistoryDialog"
import type { useOnboardingData } from "~/hooks/useOnboardingData"
import type { ReactNode } from "react"
import { IssueAgentActions } from "~/components/issueDetailsPage/IssueAgentActions"
import { IssueBlockers } from "~/components/issueDetailsPage/IssueBlockers"
import { IssueProjectPanel } from "~/components/issueDetailsPage/IssueProjectPanel"
import { difficultyLabel } from "~/lib/difficulty"
import { IssuePropertiesFields } from "~/components/issueDetailsPage/IssuePropertiesFields"
import { CommentSurface } from "~/components/activity/Comments"
import { RelativeTimestamp } from "~/components/RelativeTimestamp"
import { CommentHeader } from "~/components/activity/Comments"
import { CommentBody } from "~/components/activity/Comments"
import { Badge } from "~/components/ui/badge"
import { Card } from "~/components/ui/card"
import { ActivityTimelineItem } from "~/components/activity/Comments"
import { TooltipButton } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { format, formatDistanceToNow } from "date-fns"
import {
	CheckCircle2Icon,
	TagsIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	CopyIcon,
	EllipsisIcon,
	LinkIcon,
	MessageCircleReplyIcon,
	Maximize2Icon,
	XIcon,
	SmilePlusIcon,
	Trash2Icon,
	UserCircleIcon
} from "lucide-react"
import { useEffect, useId, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Link } from "~/components/Link"
import { toast } from "sonner"
import { CommentComposer, CommentContent } from "~/components/activity/Comments"
import { AppHeader } from "~/components/AppHeader"
import { DetailsRow, DetailsSection } from "~/components/details/DetailsSection"
import { Editor } from "~/components/editor/Editor"
import { Capacitor } from "@capacitor/core"
import { CollaborativeEditor } from "~/components/editor/CollaborativeEditor"
import type { MentionAutocompleteUser } from "~/components/editor/plugins/MentionAutocomplete/utils"

import { IssueGitHubPanel } from "~/components/issueDetailsPage/IssueGitHubPanel"
import { IssueLabelPicker } from "~/components/issueDetailsPage/IssueLabelPicker"
import {
	IssueLabelAddButton,
	IssueLabelShowcase
} from "~/components/issueDetailsPage/IssueLabelShowcase"
import {
	IssueSetting,
	issueSettingAssignees
} from "~/components/issueDetailsPage/IssueSetting"
import { IssueFavoriteButton } from "~/components/issueDetailsPage/IssueFavoriteButton"
import { IssueWatchers } from "~/components/issueDetailsPage/IssueWatchers"
import { QueueReviewButton } from "~/components/issueDetailsPage/QueueReviewButton"
import { StatusIcon } from "~/components/icons/status"
import { IssueWatchButton } from "~/components/issueDetailsPage/IssueWatchButton"
import { LoadingState } from "~/components/screens/StateScreen"
import { UnsavedChanges } from "~/components/UnsavedChanges"
import { useWorkspaceTab } from "~/components/workspace/Workspace"
import {
	UserHoverCard,
	type UserHoverCardMember
} from "~/components/UserHoverCard"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button } from "~/components/ui/button"
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from "~/components/ui/collapsible"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from "~/components/ui/dropdown-menu"
import { DetailsLayout } from "~/components/details/DetailsLayout"
import { useSidebar } from "~/components/ui/sidebar"
import { TooltipProvider } from "~/components/ui/tooltip"
import { useOrganization } from "~/hooks/useOrganization"
import { usePermission } from "~/hooks/usePermission"
import { useTeam } from "~/hooks/useTeam"
import { authClient } from "~/lib/auth-client"
import {
	IssueStatus,
	type IssueCommentReactionType,
	type IssueDifficulty,
	type IssuePriority
} from "~/lib/enums"
import { getAppUrl } from "~/lib/client-url"
import { confirmAction } from "~/lib/native-dialog"
import { shareUrl } from "~/lib/native-share"
import { TeamPermissions } from "~/lib/permissions"
import {
	cn,
	enumFormat,
	fancyIssueId,
	getEditorMarkdown,
	getEditorPlainText,
	normalizeIssueDescription,
	normalizeEditorContent,
	textToEditorContent
} from "~/lib/utils"
import { api, getOrbitClientId, type RouterOutputs } from "~/trpc/react"

type ActivityComment = NonNullable<
	RouterOutputs["issues"]["get"]
>["comments"][number] & {
	replies: ActivityComment[]
}

const COMMENT_REACTIONS: {
	type: IssueCommentReactionType
	label: string
	emoji: string
}[] = [
	{
		type: "ThumbsUp",
		label: "Thumbs up",
		emoji: "👍"
	},
	{
		type: "ThumbsDown",
		label: "Thumbs down",
		emoji: "👎"
	},
	{
		type: "PlusOne",
		label: "Plus one",
		emoji: "➕"
	},
	{
		type: "Rocket",
		label: "Rocket",
		emoji: "🚀"
	},
	{
		type: "Fire",
		label: "Fire",
		emoji: "🔥"
	},
	{
		type: "Heart",
		label: "Heart",
		emoji: "❤️"
	}
]

function serializeActivityValue(value: unknown) {
	return JSON.stringify(value, (_key, nested) =>
		Array.isArray(nested) ? [...nested].sort() : nested
	)
}

function getReactionTooltip({
	names,
	emoji
}: {
	names: string[]
	emoji: string
}) {
	if (names.length === 0) return ""
	if (names.length === 1) return `${names[0]} reacted with ${emoji}`
	if (names.length === 2) return `${names.join(" and ")} reacted with ${emoji}`
	return `${names.slice(0, -1).join(", ")}, and ${names.at(-1)} reacted with ${emoji}`
}

function ActivityCommentThread({
	comment,
	canComment,
	canResolve,
	currentOrgMemberId,
	membersByOrgMemberId,
	mentionUsers,
	formatRelativeTimestamp,
	onCopyContent,
	onCopyLink,
	onReply,
	onResolve,
	onReaction,
	depth = 0,
	replyComposerId,
	replyText,
	setReplyComposerId,
	setReplyText,
	isReplyPending,
	referencesEnabled = true
}: {
	comment: ActivityComment
	referencesEnabled?: boolean
	canComment: boolean
	canResolve: boolean
	currentOrgMemberId?: string
	membersByOrgMemberId: Map<string, UserHoverCardMember>
	mentionUsers: MentionAutocompleteUser[]
	formatRelativeTimestamp: (date: Date) => string
	onCopyContent: (comment: ActivityComment) => void
	onCopyLink: (comment: ActivityComment) => void
	onReply: (commentId: string, content: string) => Promise<void>
	onResolve: (comment: ActivityComment, isResolved: boolean) => void
	onReaction: (comment: ActivityComment, type: CommentReaction) => void
	depth?: number
	replyComposerId: string | null
	replyText: string
	setReplyComposerId: (commentId: string | null) => void
	setReplyText: (value: string) => void
	isReplyPending: boolean
}) {
	const [isCollapsed, setIsCollapsed] = useState(comment.isResolved)
	const { organization } = useOrganization()
	const customEmojis = api.emojis.list.useQuery(
		{ organizationId: organization?.id ?? "" },
		{
			enabled: !!organization && referencesEnabled,
			staleTime: 30000,
			refetchInterval: 60000
		}
	)
	const reactionOptions = [
		...COMMENT_REACTIONS.map((entry) => ({
			...entry,
			customId: "",
			archived: false
		})),
		...(referencesEnabled ? (customEmojis.data ?? []) : []).map((entry) => ({
			type: `custom:${entry.id}` as const,
			label: entry.name,
			emoji: `:${entry.name}:`,
			customId: entry.id,
			archived: !!entry.archivedAt
		}))
	]
	const commenterMember = comment.orgMemberId
		? membersByOrgMemberId.get(comment.orgMemberId)
		: undefined
	const resolvedByMember = comment.resolvedByOrgMemberId
		? membersByOrgMemberId.get(comment.resolvedByOrgMemberId)
		: undefined
	const commenterName =
		comment.member?.user?.name ??
		comment.sourceEvent?.actorOverrideName ??
		"Orbit System"
	const commenterImage =
		comment.member?.user?.image ??
		comment.sourceEvent?.actorOverrideImage ??
		(comment.member ? null : "/logo.png")
	const resolvedByName = comment.resolvedBy?.user?.name ?? "Unknown user"
	const isReplying = replyComposerId === comment.id

	useEffect(() => {
		if (comment.isResolved) {
			setIsCollapsed(true)
		} else {
			setIsCollapsed(false)
		}
	}, [comment.isResolved])

	const submitReply = async () => {
		const content = normalizeEditorContent(replyText)
		if (!getEditorPlainText(content).trim()) return
		await onReply(comment.id, content)
		setReplyText(textToEditorContent(null))
		setReplyComposerId(null)
	}

	return (
		<div className={cn("min-w-0 max-w-full", depth > 0 && "mt-2")}>
			<CommentSurface
				id={`comment-${comment.id}`}
				resolved={comment.isResolved}
			>
				<Collapsible open={!isCollapsed}>
					{comment.isResolved && (
						<div className="border-b border-stroke/70 px-3 py-2 pl-4 text-xs text-text-small">
							<span className="font-medium text-orbit-text">System</span>
							<span>: Comment marked as resolved by </span>
							<UserHoverCard member={resolvedByMember}>
								<span className="mr-1 font-medium text-text-main">
									{resolvedByName}
								</span>
							</UserHoverCard>
							{comment.resolvedAt && (
								<RelativeTimestamp
									date={comment.resolvedAt}
									formatRelativeTimestamp={formatRelativeTimestamp}
									className="ml-1"
								/>
							)}
						</div>
					)}
					<CommentHeader>
						<div className="min-w-0 flex-1">
							<div className="flex min-w-0 items-center gap-2">
								<EntityIdentity
									kind="user"
									member={commenterMember}
									name={commenterName}
									image={commenterImage}
									lastSeenAt={commenterMember?.lastSeenAt}
									size="md"
								/>
								<RelativeTimestamp
									date={comment.createdAt}
									formatRelativeTimestamp={formatRelativeTimestamp}
								/>
							</div>
							{isCollapsed && (
								<p className="mt-1 truncate text-sm text-text-small">
									{getEditorPlainText(comment.content)}
								</p>
							)}
						</div>
						<div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover/comment:opacity-100 group-focus-within/comment:opacity-100">
							{comment.isResolved && (
								<CheckCircle2Icon className="size-4 text-green-400" />
							)}
							{canComment && !comment.isResolved && (
								<Button
									variant="ghost"
									size="compact"
									className="opacity-0 transition-opacity group-hover/comment:opacity-100 group-focus-within/comment:opacity-100"
									onClick={() => {
										setReplyComposerId(isReplying ? null : comment.id)
										setReplyText(textToEditorContent(null))
									}}
									shape="pill"
								>
									<MessageCircleReplyIcon className="size-3.5" />
									Reply
								</Button>
							)}
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										type="button"

										disabled={!canComment || !currentOrgMemberId}
										aria-label="Add reaction"
										variant="ghost"
										size="icon-sm"
										shape="pill"
									>
										<SmilePlusIcon className="size-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end" className="min-w-40">
									{reactionOptions
										.filter((entry) => !entry.archived)
										.map(({ type, label, emoji, customId }) => (
											<DropdownMenuItem
												key={type}
												onClick={() => onReaction(comment, type)}
											>
												<span className="text-base">
													{customId ? (
														<CustomEmoji id={customId} name={label} />
													) : (
														emoji
													)}
												</span>
												{label}
											</DropdownMenuItem>
										))}
								</DropdownMenuContent>
							</DropdownMenu>
							<CollapsibleTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => setIsCollapsed((value) => !value)}
									aria-label={
										isCollapsed ? "Expand comment" : "Collapse comment"
									}
								>
									{isCollapsed ? (
										<ChevronRightIcon className="size-4" />
									) : (
										<ChevronDownIcon className="size-4" />
									)}
								</Button>
							</CollapsibleTrigger>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										aria-label="Comment actions"
									>
										<EllipsisIcon className="size-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem onClick={() => onCopyLink(comment)}>
										<LinkIcon className="size-4" />
										Copy link
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => onCopyContent(comment)}>
										<CopyIcon className="size-4" />
										Copy content
									</DropdownMenuItem>
									{canResolve && (
										<>
											<DropdownMenuSeparator />
											<DropdownMenuItem
												onClick={() => onResolve(comment, !comment.isResolved)}
											>
												<CheckCircle2Icon className="size-4" />
												{comment.isResolved
													? "Mark unresolved"
													: "Mark resolved"}
											</DropdownMenuItem>
										</>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					</CommentHeader>
					<CollapsibleContent>
						<CommentBody>
							<CommentContent
								content={comment.content}
								referencesEnabled={referencesEnabled}
							/>
							<div className="mt-3 flex flex-wrap items-center gap-1.5">
								{reactionOptions.map(
									({ type, emoji, customId, label, archived }) => {
										const reactions = comment.reactions.filter(
											(reaction) => reaction.type === type
										)
										if (reactions.length === 0) return null

										const isActive = reactions.some(
											(reaction) => reaction.orgMemberId === currentOrgMemberId
										)
										const names = reactions.map(
											(reaction) => reaction.member.user.name ?? "Unknown user"
										)

										return (
											<TooltipButton
												variant={isActive ? "active" : "outline"}
												size="compact"
												disabled={
													!canComment ||
													!currentOrgMemberId ||
													(archived && !isActive)
												}
												onClick={() => onReaction(comment, type)}
												shape="pill"
												tooltip={getReactionTooltip({ names, emoji })}
												key={type}
											>
												<span className="text-sm">
													{customId ? (
														<CustomEmoji id={customId} name={label} />
													) : (
														emoji
													)}
												</span>
												<span className="tabular-nums">{reactions.length}</span>
											</TooltipButton>
										)
									}
								)}
							</div>
							{isReplying && !comment.isResolved && (
								<CommentComposer
									value={replyText}
									onChange={setReplyText}
									onSubmit={submitReply}
									placeholder="Write a reply..."
									disabled={!canComment}
									isPending={isReplyPending}
									submitLabel="Reply"
									className="mt-3"
									mentionUsers={mentionUsers}
									referencesEnabled={referencesEnabled}
								/>
							)}
						</CommentBody>
					</CollapsibleContent>
				</Collapsible>
			</CommentSurface>
			{comment.replies.length > 0 && (
				<div className="ml-3 mt-2 min-w-0 max-w-full space-y-2">
					{comment.replies.map((reply) => (
						<ActivityCommentThread
							key={reply.id}
							comment={reply}
							canComment={canComment}
							canResolve={canResolve}
							currentOrgMemberId={currentOrgMemberId}
							membersByOrgMemberId={membersByOrgMemberId}
							mentionUsers={mentionUsers}
							referencesEnabled={referencesEnabled}
							formatRelativeTimestamp={formatRelativeTimestamp}
							onCopyContent={onCopyContent}
							onCopyLink={onCopyLink}
							onReply={onReply}
							onResolve={onResolve}
							onReaction={onReaction}
							depth={depth + 1}
							replyComposerId={replyComposerId}
							replyText={replyText}
							setReplyComposerId={setReplyComposerId}
							setReplyText={setReplyText}
							isReplyPending={isReplyPending}
						/>
					))}
				</div>
			)}
		</div>
	)
}

export default function Page({
	previewFriendlyId,
	onClosePreview,
	practice,
	tutorialPrompt,
	tutorialActions
}: {
	previewFriendlyId?: string
	onClosePreview?: () => void
	practice?: ReturnType<typeof useOnboardingData>
	tutorialPrompt?: ReactNode
	tutorialActions?: ReactNode
} = {}) {
	const {
		orgSlug = "",
		teamSlug = "",
		issueFriendlyId: routeIssueFriendlyId = ""
	} = useParams<{
		orgSlug: string
		teamSlug: string
		issueFriendlyId: string
	}>()
	const issueFriendlyId = practice
		? String(practice.issue.friendlyId)
		: (previewFriendlyId ?? routeIssueFriendlyId)
	const { organization, isLoading: isOrgLoading } = useOrganization()
	const liveTeam = useTeam()
	const team = practice?.team ?? liveTeam.team
	const isTeamLoading = !practice && liveTeam.isLoading
	const members = practice ? [] : liveTeam.members
	const { data: session } = authClient.useSession()
	const navigate = useNavigate()
	const utils = api.useUtils()
	const appSidebar = useSidebar()

	const [issueState, setIssueState] = useState<{
		title: string
		description: string | null
		status: IssueStatus
		priority: IssuePriority
		difficulty: IssueDifficulty
		dueDate: Date | null
		assigneeId: string | null
		labelIds: string[]
	} | null>(null)
	const [commentText, setCommentText] = useState(textToEditorContent(null))
	const [replyComposerId, setReplyComposerId] = useState<string | null>(null)
	const [replyText, setReplyText] = useState(textToEditorContent(null))
	const workspaceTab = useWorkspaceTab()
	const commentDraftId = useId()
	useEffect(() => {
		workspaceTab?.setDirty(
			commentDraftId,
			!practice &&
				(!!getEditorPlainText(commentText).trim() ||
					!!getEditorPlainText(replyText).trim())
		)
	}, [workspaceTab, commentDraftId, commentText, replyText, practice])

	const issueEditScope = `issue-edit:${team?.id}:${issueFriendlyId}`
	const liveEditIssue = api.issues.edit.useMutation({
		scope: { id: issueEditScope },
		onSuccess: async (updatedIssue) => {
			const cachedIssue = updatedIssue
				? utils.issues.get.getData({
						friendlyId: String(updatedIssue.friendlyId),
						teamId: updatedIssue.teamId
					})
				: null
			await utils.issues.get.invalidate()
			await utils.github.issuePullRequests.invalidate()
			await utils.favorites.list.invalidate()
			await utils.issues.getTeamIssues.invalidate()
			await utils.projects.list.invalidate()
			if (cachedIssue?.status !== updatedIssue?.status) {
				await utils.user.getTeams.invalidate()
			}
		}
	})
	const editIssue = practice ? { mutateAsync: practice.edit } : liveEditIssue
	const pendingIssueEdits = useMutationState({
		filters: {
			status: "pending",
			predicate: (mutation) =>
				!practice && mutation.options.scope?.id === issueEditScope
		},
		select: (mutation) =>
			mutation.state.variables as Parameters<typeof liveEditIssue.mutate>[0]
	})
	const isSavingIssue = pendingIssueEdits.length > 0
	const propertySaveId = useId()
	useEffect(() => {
		workspaceTab?.setDirty(propertySaveId, isSavingIssue)
	}, [workspaceTab, propertySaveId, isSavingIssue])
	const saveDraft = api.issues.saveDraft.useMutation()
	const lastDraftPayloadRef = useRef<string | null>(null)
	const autoSaveInFlightRef = useRef(false)

	const [initialLoaded, setInitialLoaded] = useState(false)
	const loadedIssueIdRef = useRef<string | null>(null)
	const lastSyncedIssueStateRef = useRef<string | null>(null)
	const bodyRevisionRef = useRef(0)
	const manualBody = !!practice || Capacitor.isNativePlatform()

	const { hasTeamPermission } = usePermission()
	const canEdit = !!practice || hasTeamPermission(TeamPermissions.EDIT_ISSUES)
	const DescriptionEditor = practice || !canEdit ? Editor : CollaborativeEditor
	const canDelete =
		!practice && hasTeamPermission(TeamPermissions.DELETE_ISSUES)
	const canComment =
		!!practice || hasTeamPermission(TeamPermissions.COMMENT_ISSUES)
	const canResolveComments =
		!!practice || hasTeamPermission(TeamPermissions.RESOLVE_COMMENTS)

	const liveIssueQuery = api.issues.get.useQuery(
		{
			friendlyId: issueFriendlyId,
			teamId: team?.id ?? ""
		},
		{
			enabled: !practice && !!team?.id
		}
	)
	const issueQuery = practice
		? {
				data: practice.issue,
				isLoading: false,
				refetch: async () => ({ data: practice.issue })
			}
		: liveIssueQuery
	const [savedDraft, setSavedDraft] = useState<NonNullable<
		NonNullable<typeof issueQuery.data>["currentUserDraft"]
	> | null>(null)
	api.issues.onUpdate.useSubscription(
		{ teamId: team?.id ?? "", issueId: issueQuery.data?.id ?? "" },
		{
			enabled: !practice && !!team?.id && !!issueQuery.data?.id,
			onData: (update) => {
				if (
					update.actorUserId === session?.user.id &&
					update.actorClientId === getOrbitClientId()
				) {
					if (update.updatedAt) {
						const updatedAt = new Date(update.updatedAt)
						utils.issues.get.setData(
							{ friendlyId: issueFriendlyId, teamId: team?.id ?? "" },
							(cached) =>
								cached && updatedAt > cached.updatedAt
									? { ...cached, updatedAt }
									: cached
						)
					}
					return
				}
				void utils.issues.get.invalidate({
					friendlyId: issueFriendlyId,
					teamId: team?.id ?? ""
				})
				void utils.github.issuePullRequests.invalidate()
				void utils.issues.getWatchState.invalidate({
					issueId: issueQuery.data?.id ?? "",
					teamId: team?.id ?? ""
				})
			}
		}
	)

	const currentOrgMemberId = issueQuery.data?.currentOrgMemberId
	const membersByOrgMemberId = useMemo(
		() =>
			new Map(
				(issueQuery.data?.memberIdToNameMap ?? []).map((member) => [
					member.orgMemberId,
					member
				])
			),
		[issueQuery.data?.memberIdToNameMap]
	)
	const mentionUsers = useMemo(
		() =>
			(issueQuery.data?.memberIdToNameMap ?? []).map((member) => ({
				id: member.userId,
				name: member.name,
				image: member.image,
				lastSeenAt: member.lastSeenAt
			})),
		[issueQuery.data?.memberIdToNameMap]
	)

	const { data: labels } = api.labels.list.useQuery(
		{ teamId: team?.id ?? "" },
		{ enabled: !practice && !!team?.id }
	)

	const deleteIssue = api.issues.delete.useMutation({
		onSuccess: async () => {
			await utils.favorites.list.invalidate()
			await utils.issues.getTeamIssues.invalidate()
			await utils.user.getTeams.invalidate()
			navigate(`/${orgSlug}/${teamSlug}/issues`)
		}
	})

	const commentIssue = api.issues.comment.useMutation({
		onSuccess: async () => {
			setCommentText(textToEditorContent(null))
			await utils.issues.get.invalidate()
			await issueQuery.refetch()
		}
	})
	const replyToComment = api.issues.comment.useMutation({
		onSuccess: async () => {
			await utils.issues.get.invalidate()
			await issueQuery.refetch()
		}
	})
	const resolveComment = api.issues.resolveComment.useMutation({
		onSuccess: async () => {
			await utils.issues.get.invalidate()
			await issueQuery.refetch()
		}
	})
	const toggleCommentReaction = api.issues.toggleCommentReaction.useMutation({
		onSuccess: async () => {
			await utils.issues.get.invalidate()
			await issueQuery.refetch()
		}
	})

	const submitComment = async () => {
		if (practice) {
			await practice.comment(normalizeEditorContent(commentText))
			setCommentText(textToEditorContent(null))
			return
		}
		if (!issueQuery.data || !team?.id) return
		const content = normalizeEditorContent(commentText)
		if (!getEditorPlainText(content).trim()) return

		await commentIssue.mutateAsync({
			issueId: issueQuery.data.id,
			teamId: team.id,
			comment: content
		})
	}

	const submitReply = async (commentId: string, content: string) => {
		if (practice) {
			await practice.comment(content, commentId)
			return
		}
		if (!issueQuery.data || !team?.id) return

		await replyToComment.mutateAsync({
			issueId: issueQuery.data.id,
			teamId: team.id,
			comment: content,
			repliesToId: commentId
		})
	}

	const copyCommentLink = (comment: ActivityComment) => {
		const link = getAppUrl(
			practice
				? `/${orgSlug}/me/onboarding#comment-${comment.id}`
				: `/${orgSlug}/${teamSlug}/${issueFriendlyId}#comment-${comment.id}`
		)
		navigator.clipboard.writeText(link).then(() => {
			toast("Copied comment link", {
				description: link
			})
		})
	}

	const copyCommentContent = (comment: ActivityComment) => {
		navigator.clipboard
			.writeText(getEditorMarkdown(comment.content))
			.then(() => {
				toast("Copied comment content")
			})
	}

	const updateCommentResolved = (
		comment: ActivityComment,
		isResolved: boolean
	) => {
		if (practice) {
			practice.resolve(comment.id, isResolved)
			return
		}
		if (!team?.id) return

		resolveComment.mutate({
			commentId: comment.id,
			teamId: team.id,
			isResolved
		})
	}

	const updateCommentReaction = (
		comment: ActivityComment,
		type: CommentReaction
	) => {
		if (!team?.id) return

		if (practice) {
			practice.react(comment.id, type)
			return
		}
		toggleCommentReaction.mutate({
			commentId: comment.id,
			teamId: team.id,
			type
		})
	}

	const formatRelativeTimestamp = (date: Date) => {
		const isWithinYear = Date.now() - date.getTime() < 365 * 24 * 60 * 60 * 1000

		if (!isWithinYear) {
			return format(date, "d MMM yyyy, HH:mm 'UTC'")
		}

		return formatDistanceToNow(date, { addSuffix: true })
	}

	const renderActivityEventText = (
		event: NonNullable<typeof issueQuery.data>["events"][number]
	) => {
		const oldValue = event.oldValue as Record<string, unknown> | null
		const newValue = event.newValue as Record<string, unknown> | null

		switch (event.type) {
			case "Create":
				return "created the issue"
			case "Delete":
				return "deleted the issue"
			case "Restore":
				return "restored the issue"
			case "TitleChange":
				return "changed the title"
			case "DescriptionChange":
				return "changed the description"
			case "StatusChange":
				return `changed status to ${enumFormat(String(newValue?.status ?? ""))}${oldValue?.status === IssueStatus.Queue ? " from the Queue" : ""}`
			case "PriorityChange":
				return `changed priority to ${enumFormat(String(newValue?.priority ?? ""))}`
			case "DifficultyChange":
				return `changed difficulty to ${difficultyLabel(newValue?.difficulty == null ? null : Number(newValue.difficulty), team?.difficulties)}`
			case "DueDateChange":
				return newValue?.dueDate
					? `changed due date to ${format(new Date(String(newValue.dueDate)), "PPP")}`
					: "cleared the due date"
			case "BlockerChange":
				return `${newValue?.linked ? "added" : "removed"} blocker ${fancyIssueId(Number(newValue?.blockerFriendlyId), teamSlug)}: ${newValue?.blockerTitle}`
			case "TeamChange":
				return `transferred the issue from ${oldValue?.teamName ?? "another team"} to ${newValue?.teamName ?? "another team"}`
			case "ProjectChange":
				return newValue?.projectName
					? `changed project to ${newValue.projectName}`
					: "removed the project"
			case "AssigneeChange": {
				const assigneeId = String(newValue?.assigneeId ?? "")
				const assigneeName = issueQuery.data?.memberIdToNameMap.find(
					(member) => member.id === assigneeId
				)?.name
				return `changed assignee to ${assigneeName ?? "unassigned"}`
			}
			case "LabelChange": {
				const oldLabelIds = new Set(
					((oldValue?.labelIds as string[]) ?? []).filter(Boolean)
				)
				const newLabelIds = new Set(
					((newValue?.labelIds as string[]) ?? []).filter(Boolean)
				)
				const added = (issueQuery.data?.teamLabels ?? [])
					.filter(
						(label) => newLabelIds.has(label.id) && !oldLabelIds.has(label.id)
					)
					.map((label) => label.name)
				const removed = (issueQuery.data?.teamLabels ?? [])
					.filter(
						(label) => oldLabelIds.has(label.id) && !newLabelIds.has(label.id)
					)
					.map((label) => label.name)
				if (added.length > 0 && removed.length > 0) {
					return `updated labels (+${added.join(", ")} / -${removed.join(", ")})`
				}
				if (added.length > 0) {
					return `added labels: ${added.join(", ")}`
				}
				if (removed.length > 0) {
					return `removed labels: ${removed.join(", ")}`
				}
				return "updated labels"
			}
			default:
				return "updated the issue"
		}
	}

	const activityItems = useMemo(() => {
		if (!issueQuery.data) return []

		const commentsById = new Map<string, ActivityComment>()
		for (const comment of issueQuery.data.comments) {
			commentsById.set(comment.id, { ...comment, replies: [] })
		}

		const topLevelComments: ActivityComment[] = []
		for (const comment of commentsById.values()) {
			if (comment.repliesToId) {
				const parentComment = commentsById.get(comment.repliesToId)
				if (parentComment) {
					parentComment.replies.push(comment)
				} else {
					topLevelComments.push(comment)
				}
			} else {
				topLevelComments.push(comment)
			}
		}

		const items = [
			...issueQuery.data.events
				.filter((event) => event.type !== "Comment")
				.map((event) => ({ type: "event" as const, event })),
			...topLevelComments.map((comment) => ({
				type: "comment" as const,
				comment
			}))
		].sort((a, b) => {
			const aTime =
				a.type === "event"
					? a.event.createdAt.getTime()
					: a.comment.createdAt.getTime()
			const bTime =
				b.type === "event"
					? b.event.createdAt.getTime()
					: b.comment.createdAt.getTime()
			return aTime - bTime
		})
		const propertyTypes = new Set([
			"StatusChange",
			"PriorityChange",
			"DifficultyChange",
			"DueDateChange",
			"AssigneeChange",
			"LabelChange"
		])
		const visible: typeof items = []
		const sessionProperties = new Map<string, number>()
		let actor: string | null = null
		let sessionStartedAt = 0
		for (const item of items) {
			if (
				item.type !== "event" ||
				!propertyTypes.has(item.event.type) ||
				(item.event.type === "StatusChange" &&
					((item.event.oldValue as Record<string, unknown> | null)?.status ===
						IssueStatus.Queue ||
						(item.event.newValue as Record<string, unknown> | null)?.status ===
							IssueStatus.Queue)) ||
				!item.event.actorOrgMemberId ||
				item.event.actorIsSystem
			) {
				sessionProperties.clear()
				actor = null
				visible.push(item)
				continue
			}
			const time = item.event.createdAt.getTime()
			if (
				actor !== item.event.actorOrgMemberId ||
				time - sessionStartedAt > 300_000 ||
				issueQuery.data.comments.some(
					(comment) =>
						comment.createdAt.getTime() > sessionStartedAt &&
						comment.createdAt.getTime() <= time
				)
			) {
				sessionProperties.clear()
				actor = item.event.actorOrgMemberId
				sessionStartedAt = time
			}
			const index = sessionProperties.get(item.event.type)
			const previous = index === undefined ? undefined : visible[index]
			if (index !== undefined && previous?.type === "event") {
				// Only merge a continuous value chain; don't bridge missing/system changes.
				if (
					serializeActivityValue(previous.event.newValue) ===
					serializeActivityValue(item.event.oldValue)
				) {
					visible[index] = {
						...item,
						event: { ...item.event, oldValue: previous.event.oldValue }
					}
					continue
				}
			}
			sessionProperties.set(item.event.type, visible.length)
			visible.push(item)
		}
		return visible
			.filter(
				(item) =>
					item.type !== "event" ||
					!propertyTypes.has(item.event.type) ||
					serializeActivityValue(item.event.oldValue) !==
						serializeActivityValue(item.event.newValue)
			)
			.sort(
				(a, b) =>
					(a.type === "event"
						? a.event.createdAt
						: a.comment.createdAt
					).getTime() -
					(b.type === "event"
						? b.event.createdAt
						: b.comment.createdAt
					).getTime()
			)
	}, [issueQuery.data])

	const issueStateFingerprint = useMemo(() => {
		if (!issueState || !issueQuery.data || !team?.id) return null

		return JSON.stringify({
			id: issueQuery.data.id,
			teamId: team.id,
			title: issueState.title,
			description: manualBody
				? getEditorContentFingerprint(issueState.description)
				: undefined
		})
	}, [issueState, issueQuery.data, team?.id])

	const issueDataFingerprint = useMemo(() => {
		if (!issueQuery.data || !team?.id) return null

		return JSON.stringify({
			id: issueQuery.data.id,
			teamId: team.id,
			title: issueQuery.data.title,
			description: manualBody
				? getEditorContentFingerprint(issueQuery.data.description)
				: undefined
		})
	}, [issueQuery.data, team?.id])

	const hasChanged = useMemo(() => {
		if (!initialLoaded || !issueStateFingerprint || !issueDataFingerprint)
			return false

		return issueStateFingerprint !== issueDataFingerprint
	}, [initialLoaded, issueStateFingerprint, issueDataFingerprint])

	const draftPayload = useMemo(() => {
		if (!issueState || !issueQuery.data || !team?.id) return null
		return {
			id: issueQuery.data.id,
			teamId: team.id,
			title: issueState.title,
			status: issueQuery.data.status,
			priority: issueQuery.data.priority,
			difficulty: issueQuery.data.difficulty,
			dueDate: issueQuery.data.dueDate ?? null,
			assigneeId: issueQuery.data.assignee?.id ?? null,
			labelIds: issueQuery.data.labels.map((label) => label.id)
		}
	}, [issueState, issueQuery.data, team?.id])

	const draftPayloadFingerprint = useMemo(() => {
		if (!draftPayload) return null

		return JSON.stringify({
			...draftPayload,
			dueDate: draftPayload.dueDate?.getTime() ?? null,
			labelIds: [...draftPayload.labelIds].sort()
		})
	}, [draftPayload])
	useEffect(() => {
		if (!issueQuery.data || !team?.id || !issueDataFingerprint) return

		const issueChanged = loadedIssueIdRef.current !== issueQuery.data.id
		const hasLocalChanges =
			issueStateFingerprint !== null &&
			lastSyncedIssueStateRef.current !== null &&
			issueStateFingerprint !== lastSyncedIssueStateRef.current

		if (initialLoaded && !issueChanged && hasLocalChanges) return

		const draft = issueQuery.data.currentUserDraft
		const nextIssueState = {
			title: issueQuery.data.title,
			description: normalizeIssueDescription(issueQuery.data.description),
			status: issueQuery.data.status,
			priority: issueQuery.data.priority,
			difficulty: issueQuery.data.difficulty,
			dueDate: issueQuery.data.dueDate ?? null,
			assigneeId: issueQuery.data.assignee?.id ?? null,
			labelIds: issueQuery.data.labels.map((label) => label.id)
		}

		setIssueState(nextIssueState)
		bodyRevisionRef.current = liveIssueQuery.data?.descriptionRevision ?? 0
		lastSyncedIssueStateRef.current = issueDataFingerprint
		loadedIssueIdRef.current = issueQuery.data.id
		lastDraftPayloadRef.current = draft
			? JSON.stringify({
					id: issueQuery.data.id,
					teamId: team.id,
					title: draft.title,
					status: draft.status,
					priority: draft.priority,
					difficulty: draft.difficulty,
					dueDate: draft.dueDate?.getTime() ?? null,
					assigneeId: draft.assigneeId,
					labelIds: [...draft.labelIds].sort()
				})
			: null
		setSavedDraft(draft ?? null)
		setInitialLoaded(true)
	}, [
		issueQuery.data,
		initialLoaded,
		issueDataFingerprint,
		issueStateFingerprint,
		team?.id
	])

	useEffect(() => {
		if (!issueQuery.isLoading && issueQuery.data === null) {
			if (onClosePreview) onClosePreview()
			else navigate(`/${orgSlug}/${teamSlug}/issues`)
		}
	}, [
		issueQuery.isLoading,
		issueQuery.data,
		navigate,
		orgSlug,
		teamSlug,
		onClosePreview
	])

	useEffect(() => {
		if (practice || !canEdit || !hasChanged || !draftPayload?.title.trim()) {
			return
		}
		const interval = window.setInterval(() => {
			if (!draftPayloadFingerprint) return
			const nextDraftPayload = draftPayloadFingerprint
			if (
				autoSaveInFlightRef.current ||
				lastDraftPayloadRef.current === nextDraftPayload
			) {
				return
			}
			autoSaveInFlightRef.current = true
			void saveDraft
				.mutateAsync(draftPayload)
				.then((draft) => {
					lastDraftPayloadRef.current = nextDraftPayload
					setSavedDraft(draft ?? null)
				})
				.catch((error) => {
					toast.error("Failed to auto-save draft", {
						description: `${error}`
					})
				})
				.finally(() => {
					autoSaveInFlightRef.current = false
				})
		}, 5000)
		return () => window.clearInterval(interval)
	}, [
		practice,
		canEdit,
		hasChanged,
		draftPayload,
		draftPayloadFingerprint,
		saveDraft
	])

	if (issueQuery.isLoading || isOrgLoading || isTeamLoading) {
		return <LoadingState className="bg-transparent" />
	}

	const reset = async () => {
		const newFetch = await issueQuery.refetch()

		if (newFetch.data && team?.id) {
			const description = normalizeIssueDescription(newFetch.data.description)
			setIssueState({
				title: newFetch.data.title,
				description,
				status: newFetch.data.status,
				priority: newFetch.data.priority,
				difficulty: newFetch.data.difficulty,
				dueDate: newFetch.data.dueDate ?? null,
				assigneeId: newFetch.data.assignee?.id ?? null,
				labelIds: newFetch.data.labels.map((label) => label.id)
			})
			lastSyncedIssueStateRef.current = JSON.stringify({
				id: newFetch.data.id,
				teamId: team.id,
				title: newFetch.data.title,
				description: manualBody
					? getEditorContentFingerprint(description)
					: undefined
			})
			bodyRevisionRef.current =
				"descriptionRevision" in newFetch.data
					? newFetch.data.descriptionRevision
					: 0
			loadedIssueIdRef.current = newFetch.data.id
		}
		setInitialLoaded(true)
	}

	const restoreDraft = async () => {
		const draft = savedDraft
		if (!draft) return
		const description = issueState
			? issueState.description
			: normalizeIssueDescription(issueQuery.data?.description)
		setIssueState({
			title: draft.title,
			description,
			status: issueQuery.data!.status,
			priority: issueQuery.data!.priority,
			difficulty: issueQuery.data!.difficulty,
			dueDate: issueQuery.data!.dueDate ?? null,
			assigneeId: issueQuery.data!.assignee?.id ?? null,
			labelIds: issueQuery.data!.labels.map((label) => label.id)
		})
	}

	if (!issueQuery.data || issueState === null) return null
	const savedIssue = issueQuery.data
	// Overlay queued edits in order without touching the title/body draft.
	const issueProperties = pendingIssueEdits.reduce(
		(properties, change) => ({ ...properties, ...change }),
		{
			status: savedIssue.status,
			priority: savedIssue.priority,
			difficulty: savedIssue.difficulty,
			dueDate: savedIssue.dueDate ?? null,
			assigneeId: savedIssue.assignee?.id ?? null,
			labelIds: savedIssue.labels.map((label) => label.id),
			projectId: savedIssue.projectId
		}
	)
	const saveProperties = (
		change: Partial<
			Omit<NonNullable<typeof issueState>, "title" | "description"> & {
				projectId: string | null
			}
		>
	) => {
		if (!canEdit) return
		void editIssue
			.mutateAsync({
				id: savedIssue.id,
				teamId: team?.id ?? "",
				...change
			})
			.catch((error: { message: string }) => {
				toast.error("Failed to save issue", { description: error.message })
			})
	}
	const detailsContent = (
		<>
			<div
				data-onboarding-target={practice ? "properties" : undefined}
				className="flex flex-col gap-4"
			>
				<div className="flex flex-col gap-4">
					<Card variant="outline" density="flush">
						<DetailsSection title="Workflow" variant="grouped">
							<IssuePropertiesFields
								requireTeam={!practice}
								value={issueProperties}
								onChange={saveProperties}
								disabled={!canEdit}
								activeTeamId={team?.id}
								labeled
							/>
						</DetailsSection>

						{team?.enableProjects && (
							<DetailsSection variant="grouped">
								<IssueProjectPanel
									teamId={team?.id ?? ""}
									projectId={issueProperties.projectId}
									orgSlug={orgSlug}
									teamSlug={teamSlug}
									onChange={(projectId) => saveProperties({ projectId })}
									disabled={!canEdit}
								/>
							</DetailsSection>
						)}
						<DetailsSection title="Ownership" variant="grouped">
							<DetailsRow label="Assignee">
								<IssueSetting<typeof issueState.assigneeId>
									requireTeam={!practice}
									disabled={!canEdit}
									value={issueProperties.assigneeId}
									onChange={(assigneeId) => saveProperties({ assigneeId })}
									placeholder={
										<>
											<UserCircleIcon className="size-4 text-icons" />
											<span>Assignee</span>
										</>
									}
									options={issueSettingAssignees(members)}
									showArrow
									showBorder
								/>
							</DetailsRow>
						</DetailsSection>
					</Card>

					{team?.enableProjects && (
						<IssueBlockers
							issueId={savedIssue.id}
							teamId={team?.id ?? ""}
							teamSlug={teamSlug}
							orgSlug={orgSlug}
							canEdit={canEdit}
						/>
					)}
					<IssueGitHubPanel
						issueId={savedIssue.id}
						teamId={team?.id ?? ""}
						githubRepo={issueQuery.data.githubRepo}
						githubIssueNumber={issueQuery.data.githubIssueNumber}
						pullRequests={issueQuery.data.pullRequests}
					/>

					<DetailsSection
						title="Labels"
						action={
							<IssueLabelPicker
								disabled={!canEdit}
								labels={
									labels?.map((label) => ({
										value: `${label.id}¦${label.name}`,
										data: {
											name: label.name,
											color: label.color
										}
									})) ?? []
								}
								selected={issueProperties.labelIds}
								onChange={(labelIds) => saveProperties({ labelIds })}
								withSearch
							>
								<IssueLabelAddButton
									disabled={!canEdit}
									aria-label="Add label"
								/>
							</IssueLabelPicker>
						}
					>
						<IssueLabelShowcase
							labels={(labels || []).filter((label) =>
								issueProperties.labelIds.includes(label.id)
							)}
							truncateNamesTo={24}
							onRemove={
								canEdit
									? (labelId) => {
											saveProperties({
												labelIds: issueProperties.labelIds.filter(
													(id) => id !== labelId
												)
											})
										}
									: undefined
							}
						/>
					</DetailsSection>
				</div>
			</div>
		</>
	)
	return (
		<main className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
			<DetailsLayout
				title="Issue details"
				compactMode="inline"
				details={detailsContent}
				forceSheet={!!previewFriendlyId}
				header={(detailsToggle) => (
					<TooltipProvider>
						<AppHeader
							sidebarToggle={
								previewFriendlyId ? undefined : appSidebar.toggleSidebar
							}
							triggerClassName={previewFriendlyId ? "hidden" : undefined}
							actions={
								<>
									{tutorialActions}
									{!practice && liveIssueQuery.data && (
										<IssueHistoryDialog
											key={savedIssue.id}
											issue={liveIssueQuery.data}
											canRestore={canEdit}
											hasUnsavedChanges={hasChanged}
											saving={isSavingIssue}
										/>
									)}
									{!practice && (
										<IssueFavoriteButton
											issueId={savedIssue.id}
											teamId={team?.id ?? ""}
										/>
									)}
									{!practice && savedIssue?.id && team?.id && (
										<IssueWatchButton
											issueId={savedIssue.id}
											teamId={team.id}
										/>
									)}
									{!practice && team && (
										<IssueAgentActions
											issue={savedIssue}
											team={team}
											orgSlug={orgSlug}
											authorName={session?.user.name ?? "orbit"}
										/>
									)}
									{!practice &&
										!previewFriendlyId &&
										canEdit &&
										organization &&
										team && (
											<IssueTransferDialog
												issueId={savedIssue.id}
												teamId={team.id}
												organizationId={organization.id}
												updatedAt={savedIssue.updatedAt}
												status={savedIssue.status}
												disabled={hasChanged || isSavingIssue}
											/>
										)}
									{!practice && !previewFriendlyId && (
										<>
											<TooltipButton
												variant="outline"
												size="icon-md"
												onClick={() => {
													if (!savedIssue) return
													const link = getAppUrl(
														`/${organization?.slug}/${team?.slug}/${issueFriendlyId}`
													)
													shareUrl({
														title: savedIssue.title,
														text: `${fancyIssueId(issueFriendlyId, team?.slug ?? "")} ${savedIssue.title}`,
														url: link,
														dialogTitle: "Share issue"
													}).catch(() => toast.error("Failed to share issue"))
												}}
												tooltip="Share issue"
											>
												<LinkIcon className="size-4" />
											</TooltipButton>
											{canDelete && (
												<TooltipButton
													variant="destructive-ghost"
													size="icon-md"
													disabled={deleteIssue.isPending}
													onClick={async () => {
														if (!team?.id || !savedIssue) return
														const confirmed = await confirmAction({
															title: "Delete issue",
															message: "Delete this issue?"
														})
														if (!confirmed) return
														deleteIssue.mutate({
															id: savedIssue.id,
															teamId: team.id
														})
													}}
													tooltip="Delete issue"
												>
													<Trash2Icon className="size-4" />
												</TooltipButton>
											)}
										</>
									)}
									{previewFriendlyId && (
										<>
											<TooltipButton
												asChild
												variant="outline"
												size="icon-md"
												tooltip="Open in full view"
											>
												<Link to={`/${orgSlug}/${teamSlug}/${issueFriendlyId}`}>
													<Maximize2Icon className="size-4" />
												</Link>
											</TooltipButton>
											<TooltipButton
												variant="ghost"
												size="icon-md"
												tooltip="Close preview"
												onClick={onClosePreview}
											>
												<XIcon className="size-4" />
											</TooltipButton>
										</>
									)}
									{detailsToggle}
								</>
							}
						>
							<div className="flex min-w-0 grow flex-row items-center gap-1.5 font-medium">
								<Link
									className="flex min-w-0 items-center truncate"
									to={
										practice
											? `/${orgSlug}/me/onboarding`
											: `/${orgSlug}/${teamSlug}/issues`
									}
								>
									<EntityIdentity
										kind="team"
										name={team?.name ?? "?"}
										image={team?.logo}
									/>
								</Link>
								<span className="text-text-small"> / </span>
								<span className="min-w-0 truncate text-text-main">
									{fancyIssueId(issueFriendlyId, team?.slug ?? "")}
								</span>
							</div>
						</AppHeader>
					</TooltipProvider>
				)}
			>
				{(inlineDetails) => (
					<>
						<div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-6 sm:px-6">
							<div className="mx-auto flex w-full max-w-5xl flex-col gap-7 pb-16">
								{tutorialPrompt}
								<section
									aria-label="Issue content"
									data-onboarding-target={practice ? "description" : undefined}
									className="min-w-0"
								>
									<Input
										aria-label="Issue title"
										placeholder="Issue title"
										value={issueState.title}
										onChange={(e) => {
											setIssueState({ ...issueState, title: e.target.value })
										}}
										disabled={!canEdit}
										className="mb-3 rounded-md"
										variant="title"
									/>

									{inlineDetails && (
										<div
											className="mb-5 flex flex-wrap items-center gap-2"
											data-onboarding-target={
												practice ? "properties" : undefined
											}
											aria-label="Issue properties"
										>
											<IssuePropertiesFields
												requireTeam={!practice}
												value={issueProperties}
												onChange={saveProperties}
												disabled={!canEdit}
												activeTeamId={team?.id}
												layout="pills"
											/>
											<div className="max-w-full">
												<IssueSetting
													requireTeam={!practice}
													ariaLabel="Issue assignee"
													shape="pill"
													value={issueProperties.assigneeId}
													options={issueSettingAssignees(members)}
													onChange={(assigneeId) =>
														saveProperties({ assigneeId })
													}
													disabled={!canEdit}
													showBorder
													withSearch
												/>
											</div>
											{team?.enableProjects && (
												<>
													<div className="max-w-full">
														<IssueProjectPicker
															teamId={team.id}
															value={issueProperties.projectId}
															onChange={(projectId) =>
																saveProperties({ projectId })
															}
															disabled={!canEdit}
															shape="pill"
														/>
													</div>
													<IssueBlockers
														compact
														issueId={savedIssue.id}
														teamId={team.id}
														teamSlug={teamSlug}
														orgSlug={orgSlug}
														canEdit={canEdit}
													/>
												</>
											)}
											{!practice && (
												<IssueGitHubPanel
													compact
													issueId={savedIssue.id}
													teamId={team?.id ?? ""}
													githubRepo={savedIssue.githubRepo}
													githubIssueNumber={savedIssue.githubIssueNumber}
													pullRequests={savedIssue.pullRequests}
												/>
											)}
											<IssueLabelPicker
												disabled={!canEdit}
												labels={(labels ?? []).map((label) => ({
													value: `${label.id}¦${label.name}`,
													data: { name: label.name, color: label.color }
												}))}
												selected={issueProperties.labelIds}
												onChange={(labelIds) => saveProperties({ labelIds })}
												withSearch
											>
												<Button
													variant="picker-outline"
													size="sm"
													shape="pill"
													disabled={!canEdit}
													aria-label="Issue labels"
												>
													<TagsIcon className="size-4" />
													{issueProperties.labelIds.length
														? `${issueProperties.labelIds.length} labels`
														: "No labels"}
												</Button>
											</IssueLabelPicker>
											<IssueLabelShowcase
												labels={(labels ?? []).filter((label) =>
													issueProperties.labelIds.includes(label.id)
												)}
											/>
										</div>
									)}
									<DescriptionEditor
										onSaved={() => {
											void utils.issues.get.invalidate({
												teamId: team?.id ?? "",
												friendlyId: issueFriendlyId
											})
										}}
										key={savedIssue.id}
										kind="issue"
										id={savedIssue.id}
										teamId={team?.id ?? ""}
										variant="issue"
										referencesEnabled={!practice}
										mentionUsers={practice ? [] : undefined}
										placeholder="Issue description..."
										initialState={issueState.description}
										onChange={(editorState) => {
											const nextDescription =
												normalizeIssueDescription(editorState)
											const nextFingerprint =
												getEditorContentFingerprint(nextDescription)
											setIssueState((currentIssueState) => {
												// Hydration and export-only changes are not user edits.
												if (
													!currentIssueState ||
													getEditorContentFingerprint(
														currentIssueState.description
													) === nextFingerprint
												)
													return currentIssueState
												return {
													...currentIssueState,
													description: nextDescription
												}
											})
										}}
										disabled={!canEdit}
									/>
								</section>
								{(savedIssue.status === IssueStatus.Queue ||
									!!practice?.queueDecision) &&
									(!!practice ||
										hasTeamPermission(TeamPermissions.EDIT_ISSUES)) && (
										<section aria-label="Queue review">
											<Card variant="outline" density="compact">
												<div className="flex flex-wrap items-center justify-between gap-3">
													<h2 className="flex items-center gap-2 text-sm font-medium text-text-main">
														<StatusIcon status={IssueStatus.Queue} />
														{practice?.queueDecision === "accept"
															? "Accepted into To Do"
															: "This Queue item is pending review"}
													</h2>
													<div
														role="group"
														aria-label="Queue review actions"
														className="flex items-center gap-2"
													>
														<QueueReviewButton
															practice={practice}
															issueId={savedIssue.id}
															assigneeId={savedIssue.assignee?.id ?? null}
															teamId={team?.id ?? ""}
															decision="accept"
															disabled={
																hasChanged ||
																isSavingIssue ||
																savedIssue.status !== IssueStatus.Queue
															}
															onReviewed={
																previewFriendlyId ? onClosePreview : undefined
															}
															mentionUsers={practice ? [] : mentionUsers}
														/>
														<QueueReviewButton
															practice={practice}
															issueId={savedIssue.id}
															assigneeId={savedIssue.assignee?.id ?? null}
															teamId={team?.id ?? ""}
															decision="decline"
															disabled={
																hasChanged ||
																isSavingIssue ||
																savedIssue.status !== IssueStatus.Queue
															}
															onReviewed={
																previewFriendlyId ? onClosePreview : undefined
															}
															mentionUsers={practice ? [] : mentionUsers}
														/>
													</div>
												</div>
											</Card>
										</section>
									)}
								<section className="min-w-0 max-w-full border-t border-stroke/50 pt-5">
									<div className="mb-5 flex items-center gap-3">
										<h2 className="font-semibold text-text-main">Activity</h2>
										<Badge variant="count">{activityItems.length}</Badge>
										<div className="ml-auto">
											{!practice && (
												<IssueWatchers
													issueId={savedIssue.id}
													teamId={team?.id ?? ""}
												/>
											)}
										</div>
									</div>
									<div className="mb-5 min-w-0 max-w-full">
										{activityItems.length === 0 ? (
											<p className="text-text-small">No activity yet.</p>
										) : (
											<div className="min-w-0 max-w-full pl-1">
												<div className="flex min-w-0 max-w-full flex-col gap-3">
													{activityItems.map((item, index) => {
														const isFirst = index === 0
														const isLast = index === activityItems.length - 1

														if (item.type === "event") {
															const actorMember = item.event.actorOrgMemberId
																? membersByOrgMemberId.get(
																		item.event.actorOrgMemberId
																	)
																: undefined
															const actorName =
																item.event.actor?.user?.name ??
																actorMember?.name ??
																item.event.actorOverrideName ??
																"System"

															const actorImage =
																item.event.actor?.user?.image ??
																actorMember?.image ??
																item.event.actorOverrideImage ??
																""

															return (
																<ActivityTimelineItem
																	key={item.event.id}
																	isFirst={isFirst}
																	isLast={isLast}
																	variant="event"
																>
																	<div className="flex min-w-0 max-w-full gap-2">
																		<div className="flex min-h-7 min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-sm wrap-anywhere">
																			<EntityIdentity
																				kind="user"
																				member={actorMember}
																				name={actorName}
																				image={actorImage}
																				lastSeenAt={actorMember?.lastSeenAt}
																				size="sm"
																			/>
																			<span className="min-w-0">
																				{renderActivityEventText(item.event)}
																			</span>
																			<RelativeTimestamp
																				date={item.event.createdAt}
																				formatRelativeTimestamp={
																					formatRelativeTimestamp
																				}
																			/>
																		</div>
																	</div>
																</ActivityTimelineItem>
															)
														}

														return (
															<ActivityTimelineItem
																key={item.comment.id}
																isFirst={isFirst}
																isLast={isLast}
																variant="comment"
															>
																<div className="min-w-0 max-w-full">
																	<ActivityCommentThread
																		comment={item.comment}
																		canComment={canComment}
																		canResolve={canResolveComments}
																		currentOrgMemberId={currentOrgMemberId}
																		membersByOrgMemberId={membersByOrgMemberId}
																		mentionUsers={practice ? [] : mentionUsers}
																		referencesEnabled={!practice}
																		formatRelativeTimestamp={
																			formatRelativeTimestamp
																		}
																		onCopyContent={copyCommentContent}
																		onCopyLink={copyCommentLink}
																		onReply={submitReply}
																		onResolve={updateCommentResolved}
																		onReaction={updateCommentReaction}
																		replyComposerId={replyComposerId}
																		replyText={replyText}
																		setReplyComposerId={setReplyComposerId}
																		setReplyText={setReplyText}
																		isReplyPending={replyToComment.isPending}
																	/>
																</div>
															</ActivityTimelineItem>
														)
													})}
												</div>
											</div>
										)}
									</div>
									<CommentComposer
										placeholder={
											canComment
												? "Write a comment..."
												: "You do not have permission to comment"
										}
										value={commentText}
										onChange={setCommentText}
										onSubmit={submitComment}
										disabled={!canComment}
										isPending={commentIssue.isPending}
										submitLabel="Post comment"
										mentionUsers={practice ? [] : mentionUsers}
										referencesEnabled={!practice}
									/>
								</section>
							</div>
						</div>
						<UnsavedChanges
							protectNavigation={!practice}
							isVisible={canEdit && hasChanged}
							canSave={!!issueState.title.trim()}
							pending={isSavingIssue}
							onRestoreDraft={savedDraft ? restoreDraft : undefined}
							onSave={async () => {
								if (
									lastSyncedIssueStateRef.current &&
									issueDataFingerprint !== lastSyncedIssueStateRef.current &&
									!(await confirmAction({
										title: "Issue changed",
										message:
											"This issue changed since you started editing. Save your version over the latest changes? Choose Cancel, then Reset to load the latest version."
									}))
								)
									return
								const updatedIssue = await editIssue.mutateAsync({
									id: issueQuery.data!.id,
									title: issueState.title,
									description: manualBody
										? normalizeIssueDescription(issueState.description)
										: undefined,
									descriptionRevision: manualBody
										? bodyRevisionRef.current
										: undefined,
									teamId: team?.id ?? ""
								})
								lastDraftPayloadRef.current = null
								lastSyncedIssueStateRef.current = issueStateFingerprint
								loadedIssueIdRef.current =
									updatedIssue?.id ?? issueQuery.data!.id
								setSavedDraft(null)
								setInitialLoaded(true)
							}}
							onReset={reset}
						/>
					</>
				)}
			</DetailsLayout>
		</main>
	)
}
